import React, { useCallback } from 'react';
import copy from 'clipboard-copy';
import { dispatchTaskSuccess } from '../../services/taskServices';
import { Dropdown, DropdownItem } from '../dropdown';
import { RenderImagePreview } from '../renderImagePreview';
import { BUTTON_TYPE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, IMAGE_TYPE } from '../../global/constants';
import { useMobileDevice } from '../../global/useMobile';
import Button from '../button';
import IconButton from '../button/iconButton';
import { ActionDiv } from './styles';
import Image from '../image';

interface FigmaEmbedCardProps {
  node?: {
    attribs?: {
      url?: string;
      frameNames?: string;
      projectName?: string;
      filePreviewUrl?: string;
    };
    querySelector?: (selector: string) => Element | null;
  };
  frameNames?: string;
  projectName?: string;
  filePreviewUrl?: string;
  url?: string;
  isLoading?: boolean;
  figmaLoading?: boolean;
  onClickRefreshFigma?: (figmaURL: string) => void;
}

const FigmaEmbedCard: React.FC<FigmaEmbedCardProps> = ({
  node,
  frameNames: propFrameNames,
  projectName: propProjectName,
  filePreviewUrl: propFilePreviewUrl,
  url: propUrl,
  isLoading = false,
  figmaLoading = false,
  onClickRefreshFigma
}) => {
  const mobile = useMobileDevice();

  // Get values from either props or node.attribs or by querying the node
  const frameNames =
    propFrameNames || node?.attribs?.frameNames || node?.querySelector?.('.figma-description')?.textContent || '';
  const projectName =
    propProjectName || node?.attribs?.projectName || node?.querySelector?.('.figma-filename')?.textContent || '';
  const filePreviewUrl =
    propFilePreviewUrl ||
    node?.attribs?.filePreviewUrl ||
    node?.querySelector?.('.figma-preview-image')?.getAttribute?.('src') ||
    '';
  const url = propUrl || node?.attribs?.url || node?.querySelector?.('.figma-link-div')?.getAttribute?.('href') || '';

  const onClickCopyFigmaLink = useCallback(async () => {
    await copy(url);
    dispatchTaskSuccess('Figma url copied successfully!');
  }, [url]);

  const handleOpenInFigma = useCallback(() => {
    if (url) {
      window.open(url, '_blank', 'noreferrer');
    }
  }, [url]);

  if (isLoading) {
    return (
      <div className='figma-main-div figma-loading-state'>
        <div className='figma-header figma-loading-div'>
          <div className='figma-desc-div'>
            <Image imageKey='figma_icon' alt='Figma Icon' className='figma-icon' />
            <div className='figma-text'>
              <div className='figma-description'>{frameNames}</div>
              <div className='figma-filename'>{projectName}</div>
            </div>
          </div>
          <div className='figma-right-div'>
            {!mobile && (
              <IconButton
                iconName='repeat-icon'
                size={ICON_BUTTON_SIZE.MEDIUM}
                variant={ICON_BUTTON_TYPE.TERTIARY}
                className={'refresh-icon refresh-icon-spin'}
                id='figma-refresh-ref'
              />
            )}
            <Button onClick={handleOpenInFigma} title='Open in Figma' size='small' variant={BUTTON_TYPE.SECONDARY} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='figma-main-div'>
      <div className='figma-header'>
        <div className='figma-desc-div'>
          <Image imageKey='figma_icon' alt='Figma Icon' className='figma-icon' />
          <div className='figma-text'>
            <div className='figma-description'>{frameNames}</div>
            <div className='figma-filename'>{projectName}</div>
          </div>
        </div>
        <ActionDiv className='figma-right-div'>
          {!mobile && (
            <IconButton
              id='figma-refresh-ref'
              iconName='repeat-icon'
              size={ICON_BUTTON_SIZE.MEDIUM}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              className={`refresh-icon ${figmaLoading ? 'refresh-icon-spin' : ''}`}
              onClick={() => {
                if (figmaLoading) return;
                onClickRefreshFigma?.(url);
              }}
            />
          )}
          <Button onClick={handleOpenInFigma} title='Open in Figma' size='small' variant={BUTTON_TYPE.SECONDARY} />
          <div className='three-dot-icon'>
            {mobile && (
              <IconButton
                id='figma-refresh-ref'
                iconName='repeat-icon'
                size={ICON_BUTTON_SIZE.MEDIUM}
                variant={ICON_BUTTON_TYPE.TERTIARY}
                className={'refresh-icon'}
              />
            )}
            <div className='three-dot-icon'>
              <Dropdown
                modalTitle='Share'
                isMinWidth={150}
                activeClassName='active'
                content={
                  <IconButton
                    iconName='new-three-dots-vertical-icon'
                    size={ICON_BUTTON_SIZE.MEDIUM}
                    variant={ICON_BUTTON_TYPE.TERTIARY}
                  />
                }>
                <DropdownItem
                  label='Copy link'
                  iconName='copy-link-icon'
                  iconSize={16}
                  iconViewBox='0 0 18 18'
                  iconTone='fill'
                  onClick={onClickCopyFigmaLink}
                />
              </Dropdown>
            </div>
          </div>
        </ActionDiv>
      </div>
      <div className='figma-image-container'>
        <RenderImagePreview imageUrl={filePreviewUrl} type={IMAGE_TYPE.FIGMA} altText={''} />
      </div>
    </div>
  );
};

export default FigmaEmbedCard;
